import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { hotKeywords, movieCategories } from "@/mocks/movies";
import { filterMovies } from "@/utils/movie";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const cat = params.get("cat") || "全部";
  const [input, setInput] = useState(keyword);

  const results = useMemo(
    () =>
      keyword.trim()
        ? filterMovies({ keyword, category: cat === "全部" ? undefined : cat, sort: "update" })
        : [],
    [keyword, cat]
  );

  useSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 黑桃视频` : "搜索影视作品 - 黑桃视频",
    description: keyword
      ? `黑桃视频为你找到与“${keyword}”相关的影视作品共 ${results.length} 部，可按分类进一步筛选查看详情。`
      : "在黑桃视频搜索影视作品，输入片名或关键词即可快速定位你想看的电影、电视剧与纪录片。",
    keywords: `影视搜索,${keyword || "影视作品"},黑桃视频,电影搜索,电视剧搜索`,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (cat !== "全部") next.set("cat", cat);
    setParams(next);
  };

  const changeCat = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === "全部") next.delete("cat");
    else next.set("cat", value);
    setParams(next);
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-8 md:px-8 md:pt-12">
        <h1 className="font-heading text-2xl font-600 tracking-wide text-foreground-950 md:text-3xl">
          搜索影视作品
        </h1>

        <form
          onSubmit={submit}
          className="mt-6 flex flex-col gap-3 sm:flex-row"
          role="search"
        >
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-[18px]" aria-hidden="true"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入片名或关键词，如“科幻”“悬疑”"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        {keyword && (
          <div className="mt-6 flex flex-wrap items-center gap-3 border-b border-background-200 pb-3">
            <span className="text-sm text-foreground-600">
              关键词：<span className="text-primary-500">“{keyword}”</span> · 共{" "}
              <span className="text-foreground-900">{results.length}</span> 条结果
            </span>
            <div className="flex flex-wrap gap-1.5">
              {[{ key: "全部", label: "全部" }, ...movieCategories].map((c) => {
                const active = cat === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => changeCat(c.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:text-primary-400"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {keyword && results.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center text-foreground-400">
              <i className="ri-search-eye-line text-[44px]" aria-hidden="true"></i>
            </span>
            <p className="mt-3 text-sm text-foreground-700">未找到相关影视作品</p>
            <p className="mt-1.5 text-xs text-foreground-500">换个关键词试试，或看看下面的热门搜索</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {hotKeywords.map((k) => (
                <Link
                  key={k}
                  to={`/search?q=${encodeURIComponent(k)}`}
                  className="whitespace-nowrap rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
                >
                  {k}
                </Link>
              ))}
            </div>
          </div>
        ) : keyword ? (
          <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} layout="list" />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-8 text-center">
            <p className="text-sm text-foreground-600">输入关键词开始搜索你感兴趣的影视作品。</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {hotKeywords.map((k) => (
                <Link
                  key={k}
                  to={`/search?q=${encodeURIComponent(k)}`}
                  className="whitespace-nowrap rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
                >
                  {k}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}